import { useRef, useEffect } from 'react';

interface CollapsiblePreviewProps {
  subjectPreview: string;
  htmlPreview: string;
}

export default function CollapsiblePreview({
  subjectPreview,
  htmlPreview,
}: CollapsiblePreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Auto-resize iframe after content renders
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const resizeIframe = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const height = Math.max(
          doc.body.scrollHeight,
          doc.body.offsetHeight,
          doc.documentElement.scrollHeight,
          doc.documentElement.offsetHeight
        );
        iframe.style.height = height + 'px';
      } catch {
        iframe.style.height = '600px';
      }
    };

    const handleLoad = () => {
      resizeIframe();
      timeouts.push(setTimeout(resizeIframe, 300));
      timeouts.push(setTimeout(resizeIframe, 1000));
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      iframe.removeEventListener('load', handleLoad);
      timeouts.forEach(clearTimeout);
    };
  }, [htmlPreview]);

  return (
    <div>
      {/* Subject */}
      <div className="px-6 py-4 border-b bg-gray-50">
        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Subject</p>
        <p className="text-sm font-medium text-gray-900">
          {subjectPreview || <span className="text-gray-400 italic">No subject</span>}
        </p>
      </div>

      {/* Email Body */}
      <div className="px-6 py-4">
        <iframe
          ref={iframeRef}
          srcDoc={htmlPreview}
          className="w-full border-0"
          title="Email Preview"
          sandbox="allow-same-origin"
          style={{ minHeight: '300px' }}
        />
      </div>
    </div>
  );
}
